import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./CallPage.css";

const CallPage = () => {
  const [callStatus, setCallStatus] = useState("disconnected");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [volume, setVolume] = useState(0);
  const [callDuration, setCallDuration] = useState(0);
  const [hasRedirectUrl, setHasRedirectUrl] = useState(false);
  const [callProgress, setCallProgress] = useState(0);
  const [intentionalDisconnect, setIntentionalDisconnect] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false); // ADD THIS
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [callId, setCallId] = useState(""); // ADD THIS - was missing

  const callDurationRef = useRef(null);
  const volumeIntervalRef = useRef(null);
  const remoteAudioRef = useRef(null);

  // JsSIP variables
  const callSessionRef = useRef(null);
  const uaRef = useRef(null);
  const socketRef = useRef(null);
  const localStreamRef = useRef(null);

  // Check if redirect URL is configured
  useEffect(() => {
    checkRedirectUrl();
    // Pre-request mic permission when page loads
    requestMicrophonePermission();
  }, []);

  // Enhanced disconnected event handler with proper cleanup
  useEffect(() => {
    if (uaRef.current) {
      const ua = uaRef.current;

      const handleDisconnected = () => {
        console.log("JsSIP disconnected event fired");

        // Only attempt reconnection if NOT intentional disconnect and NOT cleaning up
        if (!intentionalDisconnect && !isCleaningUp && uaRef.current) {
          console.log("Attempting automatic reconnection...");
          try {
            uaRef.current.start();
          } catch (error) {
            console.error("Reconnection failed:", error);
          }
        } else {
          console.log(
            "Disconnection was intentional or cleanup in progress, not reconnecting"
          );
        }
      };

      ua.on("disconnected", handleDisconnected);

      // Return cleanup function to prevent memory leaks
      return () => {
        // JsSIP doesn't have .off() method, but stopping the UA will clean up events
        if (ua && typeof ua.stop === "function") {
          try {
            ua.stop();
          } catch (error) {
            console.error("Error stopping UA in cleanup:", error);
          }
        }
      };
    }
  }, [intentionalDisconnect, isCleaningUp]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (callDurationRef.current) {
        clearInterval(callDurationRef.current);
      }
      if (volumeIntervalRef.current) {
        clearInterval(volumeIntervalRef.current);
      }
      // Cleanup JsSIP resources
      cleanupJsSIP();
    };
  }, []);

  const checkRedirectUrl = async () => {
    try {
      const response = await axios.get("/api/config/redirect");
      setHasRedirectUrl(!!response.data.redirectUrl);
    } catch (error) {
      console.error("Error checking redirect URL:", error);
      setHasRedirectUrl(false);
    }
  };

  const requestMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      console.log("Microphone access granted");
    } catch (err) {
      console.error("Microphone access denied:", err);
      setError("Microphone access is required for voice calls");
    }
  };

  const startCall = async () => {
    if (!hasRedirectUrl) {
      setError(
        "Please configure a redirect URL first in the Configuration page."
      );
      return;
    }

    if (!localStreamRef.current) {
      setError(
        "Microphone access is required. Please allow microphone access and try again."
      );
      return;
    }

    // Prevent starting if cleanup is in progress or resources exist
    if (uaRef.current || callSessionRef.current) {
      console.log("Cleaning up existing resources before starting new call...");
      cleanupJsSIP();
      // Small delay to ensure cleanup completes
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    try {
      setLoading(true);
      setError(null);
      setCallStatus("connecting");
      setCallProgress(0);
      setIntentionalDisconnect(false); // Reset flag when starting new call

      // Initialize JsSIP WebSocket connection
      socketRef.current = new window.JsSIP.WebSocketInterface(
        "wss://newyork.host.connexcs.net"
      );

      const configuration = {
        sockets: [socketRef.current],
        uri: "sip:12345678911@newyork.host.connexcs.net",
        password: "A1!qaz2w",
        connection_recovery_min_interval: 2,
        connection_recovery_max_interval: 30,
      };

      uaRef.current = new window.JsSIP.UA(configuration);

      // Set up event handlers
      uaRef.current.on("connected", () => {
        console.log("WebSocket connected!");
        setCallProgress(25);
        initiateSIPCall();
      });

      uaRef.current.on("disconnected", (e) => {
        console.log("WebSocket disconnected event received:", {
          callStatus,
          hasUA: !!uaRef.current,
          intentionalDisconnect,
          timestamp: new Date().toISOString(),
        });

        // Only try to restart if UA still exists, we're not in a connected call,
        // and we haven't intentionally ended the call
        if (
          uaRef.current &&
          callStatus !== "connected" &&
          callStatus !== "disconnected" &&
          !intentionalDisconnect
        ) {
          console.log("Attempting to restart UA after disconnect...");
          try {
            uaRef.current.start();
          } catch (error) {
            console.error("Failed to restart UA:", error);
          }
        } else {
          console.log(
            "Skipping UA restart - call ended, UA cleaned up, or intentional disconnect"
          );
        }
      });

      uaRef.current.on("registrationFailed", (e) => {
        console.error("Registration failed:", e);
        setError("SIP registration failed. Please try again.");
        resetUI();
      });

      // Start the user agent
      uaRef.current.start();
    } catch (error) {
      console.error("Initialization error:", error);
      setError(`Failed to initialize SIP connection: ${error.message}`);
      resetUI();
    } finally {
      setLoading(false);
    }
  };

  const initiateSIPCall = () => {
    const eventHandlers = {
      progress: () => {
        console.log("Call is in progress");
        setCallProgress(50);
        setCallStatus("connecting");
        setLoading(false);
      },
      confirmed: () => {
        console.log("Call confirmed");
        setCallProgress(100);
        setCallStatus("connected");
        startCallTimer();
        startVolumeMonitoring();
      },
      failed: (e) => {
        const cause = e.cause || e.data?.cause || "Unknown reason";
        console.log("Call failed with cause:", cause);
        setError(`Call failed: ${cause}`);
        resetUI();
      },
      ended: (e) => {
        const cause = e.cause || e.data?.cause || "Call ended";
        console.log("Call ended:", cause);
        resetUI();
      },
    };

    const pcConfig = {
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    };

    const options = {
      eventHandlers: eventHandlers,
      mediaConstraints: { audio: true, video: false },
      rtcOfferConstraints: {
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      },
      pcConfig: pcConfig,
    };

    try {
      // Check if UA is still valid before making the call
      if (!uaRef.current) {
        throw new Error("User agent is not available");
      }

      callSessionRef.current = uaRef.current.call(
        "sip:375292345678@newyork.host.connexcs.net",
        options
      );

      // Handle remote audio stream
      callSessionRef.current.connection.ontrack = (event) => {
        if (event.track.kind === "audio") {
          const audioElement = remoteAudioRef.current;
          if (audioElement) {
            audioElement.srcObject = event.streams[0];
            audioElement
              .play()
              .catch((e) => console.error("Audio play error:", e));
          }
        }
      };
    } catch (error) {
      console.error("Call initiation error:", error);
      setError(`Failed to initiate call: ${error.message}`);
      resetUI();
    }
  };

  const endCall = async () => {
    try {
      console.log("Intentional call end initiated");
      setIntentionalDisconnect(true); // Set this FIRST
      setIsCleaningUp(true); // Prevent new operations during cleanup

      if (callSessionRef.current) {
        console.log("Ending the call...");
        try {
          callSessionRef.current.terminate();
        } catch (error) {
          console.error("Error terminating call session:", error);
        }
      }

      // Clean up JsSIP completely
      cleanupJsSIP();

      // Reset UI
      resetUI();
    } catch (error) {
      console.error("Error ending call:", error);
      setError(`Failed to end call: ${error.message}`);
      // Force reset even if there's an error
      resetUI();
    }
  };

  const resetUI = () => {
    try {
      setCallStatus("Not Connected");
      setCallId(""); // NOW THIS WILL WORK
      setCallProgress(0);
      setVolumeLevel(0); // NOW THIS WILL WORK
      setCallDuration(0);
      setIntentionalDisconnect(false); // Reset flag
      setError("");

      // Clear any remaining timers
      if (callDurationRef.current) {
        clearInterval(callDurationRef.current);
        callDurationRef.current = null;
      }

      console.log("UI reset completed");
    } catch (error) {
      console.error("Error resetting UI:", error);
    }
  };

  const cleanupJsSIP = () => {
    try {
      console.log("Starting JsSIP cleanup...");

      // Remove event handlers first to prevent them from firing during cleanup
      if (uaRef.current) {
        // JsSIP doesn't have .off() method - we need to handle this differently
        // The events will be cleaned up when we stop the UA

        // Stop the user agent completely
        try {
          uaRef.current.stop();
          console.log("UA stopped successfully");
        } catch (error) {
          console.error("Error stopping UA:", error);
        }

        // Clear the reference
        uaRef.current = null;
      }

      if (callSessionRef.current) {
        try {
          callSessionRef.current.terminate();
        } catch (error) {
          console.error("Error terminating call session:", error);
        }
        callSessionRef.current = null;
      }

      if (socketRef.current) {
        try {
          socketRef.current.close();
        } catch (error) {
          console.error("Error closing socket:", error);
        }
        socketRef.current = null;
      }

      if (localStreamRef.current) {
        try {
          localStreamRef.current.getTracks().forEach((track) => track.stop());
        } catch (error) {
          console.error("Error stopping local stream tracks:", error);
        }
        localStreamRef.current = null;
      }

      console.log("JsSIP cleanup completed successfully");
    } catch (error) {
      console.error("Error during JsSIP cleanup:", error);
    } finally {
      setIsCleaningUp(false); // Allow new operations
    }
  };

  const startCallTimer = () => {
    setCallDuration(0);
    callDurationRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  };

  const stopCallTimer = () => {
    if (callDurationRef.current) {
      clearInterval(callDurationRef.current);
      callDurationRef.current = null;
    }
  };

  const startVolumeMonitoring = () => {
    volumeIntervalRef.current = setInterval(() => {
      // Simulate volume changes for visual feedback
      if (callStatus === "connected") {
        setVolume((prev) => Math.max(0, prev + (Math.random() - 0.5) * 10));
      }
    }, 100);
  };

  const stopVolumeMonitoring = () => {
    if (volumeIntervalRef.current) {
      clearInterval(volumeIntervalRef.current);
      volumeIntervalRef.current = null;
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const getStatusColor = () => {
    switch (callStatus) {
      case "connected":
        return "#48bb78";
      case "connecting":
        return "#ed8936";
      case "disconnected":
        return "#e53e3e";
      default:
        return "#718096";
    }
  };

  const getStatusText = () => {
    switch (callStatus) {
      case "connected":
        return "Call Active";
      case "connecting":
        return "Connecting...";
      case "disconnected":
        return "Not Connected";
      default:
        return "Unknown";
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h2>📞 Voice Call</h2>

        <div className="call-description">
          <p>
            This voice calling system uses WebRTC SIP technology to connect
            directly to the AI voice agent. All requests and responses are also
            sent to your configured redirect URL via POST JSON for additional
            processing and logging.
          </p>
        </div>

        {!hasRedirectUrl && (
          <div className="status error">
            ⚠️ Please configure a redirect URL in the Configuration page before
            making calls.
          </div>
        )}

        {error && <div className="status error">{error}</div>}

        <div className={`call-status ${callStatus}`}>
          <div
            className="status-indicator"
            style={{ backgroundColor: getStatusColor() }}
          ></div>
          <h3>{getStatusText()}</h3>
          {callStatus === "connected" && (
            <p className="call-duration">
              Duration: {formatDuration(callDuration)}
            </p>
          )}
        </div>

        {/* Call Progress Bar */}
        <div
          className="call-progress-container"
          style={{ display: callProgress > 0 ? "block" : "none" }}
        >
          <div className="call-progress-bar">
            <div
              className="call-progress-fill"
              style={{ width: `${callProgress}%` }}
            ></div>
          </div>
          <p className="call-progress-text">
            {callProgress === 25 && "Connecting to SIP server..."}
            {callProgress === 50 && "Initiating call..."}
            {callProgress === 100 && "Call connected"}
          </p>
        </div>

        {callStatus === "connected" && (
          <div className="volume-indicator">
            <div
              className="volume-bar"
              style={{ width: `${Math.min(100, Math.max(0, volume))}%` }}
            ></div>
            <div className="volume-label">Volume: {Math.round(volume)}%</div>
          </div>
        )}

        <div className="call-controls">
          {callStatus === "disconnected" ? (
            <button
              className="btn btn-call"
              onClick={startCall}
              disabled={loading || !hasRedirectUrl}
            >
              {loading ? "Connecting..." : "🎙️ Call Now"}
            </button>
          ) : (
            <button
              className="btn btn-danger"
              onClick={endCall}
              disabled={loading}
            >
              📞 End Call
            </button>
          )}
        </div>

        {/* Hidden audio element for remote audio */}
        <audio ref={remoteAudioRef} autoPlay style={{ display: "none" }} />

        <div className="call-info">
          <h3>Call Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <strong>Status:</strong> {getStatusText()}
            </div>
            <div className="info-item">
              <strong>Redirect URL:</strong>{" "}
              {hasRedirectUrl ? "Configured" : "Not Configured"}
            </div>
            <div className="info-item">
              <strong>WebRTC SIP:</strong>{" "}
              {callStatus === "connected" ? "Active" : "Inactive"}
            </div>
            <div className="info-item">
              <strong>Microphone:</strong>{" "}
              {localStreamRef.current ? "Access Granted" : "Access Required"}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>🔗 Architecture Details</h2>
        <div className="integration-info">
          <p>
            This system implements WebRTC SIP calling directly to the AI voice
            agent server, while also maintaining the redirect URL functionality
            for additional request/response processing. All communication with
            the remote server uses POST JSON endpoints.
          </p>

          <div className="integration-features">
            <div className="feature">
              <span className="feature-icon">🌐</span>
              <span>WebRTC SIP Voice Calling</span>
            </div>
            <div className="feature">
              <span className="feature-icon">🔄</span>
              <span>Direct SIP Server Connection</span>
            </div>
            <div className="feature">
              <span className="feature-icon">📡</span>
              <span>Real-time Voice Communication</span>
            </div>
            <div className="feature">
              <span className="feature-icon">🔒</span>
              <span>Redirect URL Integration</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallPage;
